// Mandatory-reason prompt for inquiry status transitions that need an
// explanation (PARTIALLY_FULFILLED / UNFULFILLED / CANCELLED — see
// REASON_REQUIRED_INQUIRY_STATUSES). The caller owns the PATCH: onConfirm
// receives the trimmed reason and is responsible for closing on success, so a
// failed request keeps the modal (and the typed reason) on screen.

import { RestaurantInquiryStatus } from 'apis/restaurant-inquiries';
import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from 'design-system';
import { inquiryStatusMeta } from 'pages/inquiry-processor/utils/status';
import { useEffect, useState } from 'react';

const PLACEHOLDERS: Partial<Record<RestaurantInquiryStatus, string>> = {
  [RestaurantInquiryStatus.PARTIALLY_FULFILLED]: 'e.g. Only 3 of 5 items could be sourced',
  [RestaurantInquiryStatus.UNFULFILLED]: 'e.g. No supplier could match the target price',
  [RestaurantInquiryStatus.CANCELLED]: 'e.g. Restaurant withdrew the request',
};

interface Props {
  // Target status being confirmed; null keeps the modal closed.
  status: RestaurantInquiryStatus | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
  // Extra line rendered above the reason field — e.g. the app-inquiry cancellation warning.
  notice?: string;
}

export const StatusReasonModal = ({ status, onClose, onConfirm, isLoading, notice }: Props) => {
  const toast = useToast();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (status) setReason('');
  }, [status]);

  const label = status ? inquiryStatusMeta(status).label : '';

  const confirm = () => {
    if (!reason.trim()) {
      toast({ title: `A reason is required to mark as ${label}`, status: 'warning' });
      return;
    }
    onConfirm(reason.trim());
  };

  return (
    <Modal isOpen={!!status} onClose={onClose} size={{ base: 'full', md: 'md' }}>
      <ModalOverlay />
      <ModalContent rounded='12px'>
        <ModalHeader>Mark as {label}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {notice && (
            <Text fontSize='sm' color='gray.600' mb='3'>
              {notice}
            </Text>
          )}
          <FormControl isRequired>
            <FormLabel fontSize='sm'>Reason</FormLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={status ? PLACEHOLDERS[status] : undefined}
              rows={3}
              autoFocus
            />
          </FormControl>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={confirm} isLoading={isLoading} loadingText='Saving…'>
            Mark as {label}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
